import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Get, Send, Update, Remove } from '../../services/service.api';
import { toast } from 'react-toastify';
import FullScreenLoader from '../../components/FullScreenLoader';
import AnimatedNumber from '../../components/component.animatedNumber';
import ModalCagnotte from './components/ModalCagnotte';
import ModalCotisation from './components/ModalCotisation';
import ModalOperation from './components/ModalOperation';
import {
  PiggyBank, ArrowLeft, Edit2, Trash2, Coins,
  ArrowDownCircle, UserPlus, Copy, CheckCircle2, X,
  Building2, Smartphone, ExternalLink, Crown, RefreshCw,
  AlertCircle
} from 'lucide-react';

function formatAmount(n, devise) {
  if (!n && n !== 0) return `0 ${devise || 'FCFA'}`;
  return `${Number(n).toLocaleString('fr-FR')} ${devise || 'FCFA'}`;
}

// ─── Contribution Matrix ──────────────────────────────────────────────────────
function compactAmount(n) {
  const v = Number(n || 0);
  if (v >= 1e6) return `${(v / 1e6).toFixed(v % 1e6 === 0 ? 0 : 1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(v % 1e3 === 0 ? 0 : 1)}k`;
  return String(v);
}

function ContributionMatrix({ members, cotisations, devise, isAdmin, onDelete }) {
  // Last 6 months, oldest → current
  const now = new Date();
  const currentKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    months.push({ key, label: d.toLocaleDateString('fr-FR', { month: 'short' }).replace('.', ''), year: d.toLocaleDateString('fr-FR', { year: '2-digit' }), current: key === currentKey });
  }

  // { userId: { 'YYYY-MM': [cotisations] } }
  const index = {};
  cotisations.forEach(ct => {
    if (!index[ct.user_id]) index[ct.user_id] = {};
    if (!index[ct.user_id][ct.mois]) index[ct.user_id][ct.mois] = [];
    index[ct.user_id][ct.mois].push(ct);
  });

  const sum = (list) => list.reduce((acc, ct) => acc + Number(ct.montant || 0), 0);
  const monthTotals = months.map(m => sum(cotisations.filter(ct => ct.mois === m.key)));
  const grandTotal = monthTotals.reduce((a, b) => a + b, 0);
  const maxMonth = Math.max(...monthTotals, 1);
  const paidThisMonth = members.filter(m => (index[m.id]?.[currentKey] || []).length > 0).length;
  const pct = members.length ? Math.round((paidThisMonth / members.length) * 100) : 0;

  return (
    <div className="cm">
      {/* Summary */}
      <div className="cm-summary">
        <div className="cm-summary-main">
          <div className="cm-summary-label">Ce mois-ci</div>
          <div className="cm-summary-value">
            <AnimatedNumber value={paidThisMonth} />
            <span> / {members.length} membres ont cotisé</span>
          </div>
          <div className="cm-progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className="cm-progress-fill" style={{ '--w': `${pct}%` }} />
          </div>
        </div>
        <div className="cm-summary-total">
          <div className="cm-summary-label">Total 6 mois</div>
          <div className="cm-summary-amount"><AnimatedNumber value={grandTotal} format={n => formatAmount(n, devise)} /></div>
        </div>
      </div>

      {/* Mini bar chart of monthly totals */}
      <div className="cm-spark" aria-hidden="true">
        {months.map((m, i) => (
          <div key={m.key} className={`cm-spark-col ${m.current ? 'current' : ''}`}>
            <div className="cm-spark-bar" style={{ '--h': `${Math.max((monthTotals[i] / maxMonth) * 100, monthTotals[i] > 0 ? 8 : 3)}%`, '--i': i }} />
            <span>{m.label}</span>
          </div>
        ))}
      </div>

      {/* Member grid */}
      <div className="cm-grid" role="table">
        <div className="cm-row cm-head" role="row">
          <div className="cm-member-col" role="columnheader">Membre</div>
          <div className="cm-months">
            {months.map(m => (
              <div key={m.key} className={`cm-month-head ${m.current ? 'current' : ''}`} role="columnheader">
                {m.label}<small> {m.year}</small>
              </div>
            ))}
          </div>
          <div className="cm-total-col" role="columnheader">Total</div>
        </div>

        {members.map((member, rowIdx) => {
          const memberIndex = index[member.id] || {};
          const totalMember = sum(Object.values(memberIndex).flat());
          const paidCount = months.filter(m => (memberIndex[m.key] || []).length > 0).length;
          return (
            <div key={member.id} className="cm-row" role="row" style={{ '--i': rowIdx }}>
              <div className="cm-member" role="cell">
                <div className="cm-avatar">{(member.prenom?.[0] || '?')}{(member.nom?.[0] || '')}</div>
                <div className="cm-member-info">
                  <div className="cm-member-name">{member.prenom} {member.nom}</div>
                  <div className="cm-member-sub">{paidCount}/6 mois payés</div>
                </div>
                <div className="cm-member-total-mobile">{formatAmount(totalMember, devise)}</div>
              </div>

              <div className="cm-months">
                {months.map((m, colIdx) => {
                  const cts = memberIndex[m.key] || [];
                  const paid = cts.length > 0;
                  const state = paid ? 'paid' : m.current ? 'due' : 'missing';
                  const amount = sum(cts);
                  return (
                    <div key={m.key} className="cm-cell" role="cell">
                      <span className="cm-cell-month">{m.label} {m.year}</span>
                      <div
                        className={`cm-chip ${state}`}
                        style={{ '--d': `${rowIdx * 60 + colIdx * 35}ms` }}
                        title={paid ? formatAmount(amount, devise) : m.current ? 'En attente ce mois-ci' : 'Aucune cotisation'}
                      >
                        {paid ? (
                          <>
                            <CheckCircle2 size={13} />
                            <span>{compactAmount(amount)}</span>
                            {isAdmin && onDelete && cts.map(ct => (
                              <button
                                key={ct.id}
                                type="button"
                                className="cm-chip-remove"
                                onClick={() => onDelete(ct)}
                                aria-label="Supprimer cette cotisation"
                                title="Supprimer cette cotisation"
                              >
                                <X size={11} />
                              </button>
                            ))}
                          </>
                        ) : m.current ? (
                          <span>À payer</span>
                        ) : (
                          <span>—</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="cm-total" role="cell">
                <span className={totalMember > 0 ? 'has' : ''}>{formatAmount(totalMember, devise)}</span>
              </div>
            </div>
          );
        })}

        <div className="cm-row cm-foot" role="row">
          <div className="cm-member-col">Total / mois</div>
          <div className="cm-months">
            {months.map((m, i) => (
              <div key={m.key} className="cm-cell">
                <span className="cm-cell-month">{m.label} {m.year}</span>
                <div className={`cm-foot-value ${monthTotals[i] > 0 ? 'has' : ''}`}>{monthTotals[i] > 0 ? compactAmount(monthTotals[i]) : '—'}</div>
              </div>
            ))}
          </div>
          <div className="cm-total"><span className="has">{formatAmount(grandTotal, devise)}</span></div>
        </div>
      </div>

      <div className="cm-legend">
        <span><i className="paid" /> Payé</span>
        <span><i className="due" /> Mois en cours</span>
        <span><i className="missing" /> Manquant</span>
      </div>
    </div>
  );
}

// ─── Bank Info Card ───────────────────────────────────────────────────────────
function BankInfoCard({ cagnotte, t }) {
  const hasBankInfo = cagnotte.compte_bancaire || cagnotte.banque_nom;
  const hasMobileInfo = cagnotte.mobile_money_numero || cagnotte.mobile_money_type;

  if (!hasBankInfo && !hasMobileInfo) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 16, marginBottom: 24 }}>
      {hasBankInfo && (
        <div className="card" style={{ borderLeft: '4px solid #3B82F6' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Building2 size={16} style={{ color: '#3B82F6' }} />
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.cagnotte.bankInfo}</span>
          </div>
          {cagnotte.banque_nom && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block' }}>Banque</span>
              <span style={{ fontWeight: 600 }}>{cagnotte.banque_nom}</span>
            </div>
          )}
          {cagnotte.compte_bancaire && (
            <div>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block' }}>Numéro de compte</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.05em' }}>
                {cagnotte.compte_bancaire}
              </span>
            </div>
          )}
        </div>
      )}

      {hasMobileInfo && (
        <div className="card" style={{ borderLeft: '4px solid var(--gold)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Smartphone size={16} style={{ color: 'var(--gold)' }} />
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{t.cagnotte.mobileMoneyInfo}</span>
          </div>
          {cagnotte.mobile_money_type && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block' }}>Opérateur</span>
              <span className="badge badge-gold">{cagnotte.mobile_money_type}</span>
            </div>
          )}
          {cagnotte.mobile_money_numero && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', display: 'block' }}>Numéro</span>
              <span style={{ fontWeight: 700, fontSize: '1rem' }}>{cagnotte.mobile_money_numero}</span>
            </div>
          )}
          {cagnotte.mobile_money_lien && (
            <a
              href={cagnotte.mobile_money_lien}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline btn-sm"
              style={{ marginTop: 4, color: 'var(--gold)', borderColor: 'var(--gold)', display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
            >
              <ExternalLink size={13} /> Payer maintenant
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Invite Modal ──────────────────────────────────────────────────────────────
function ModalInvite({ open, onClose, onInvite }) {
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setEmail(''); setResult(null); } }, [open]);
  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSaving(true);
    try {
      const res = await onInvite(email.trim());
      setResult(res);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 420, width: '95%' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UserPlus size={18} style={{ color: 'var(--primary)' }} />
            <h3 style={{ margin: 0 }}>Inviter un membre</h3>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          {!result ? (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Email à inviter</label>
                <input id="invite-email" type="email" className="form-input" value={email} onChange={e => setEmail(e.target.value)} required placeholder="email@exemple.com" autoFocus />
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
                  Si l'email est déjà enregistré, le membre sera ajouté immédiatement.<br />
                  Sinon, un lien d'inscription lui sera envoyé par email.
                </p>
              </div>
              <div className="modal-footer" style={{ padding: 0, borderTop: 'none', marginTop: 8 }}>
                <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving || !email.trim()}>
                  {saving ? 'Envoi...' : 'Inviter'}
                </button>
              </div>
            </form>
          ) : (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              {result.type === 'added' ? (
                <>
                  <CheckCircle2 size={40} style={{ color: 'var(--success)', marginBottom: 12 }} />
                  <p style={{ fontWeight: 600, color: 'var(--success)' }}>{result.message}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Le membre a été ajouté à la cagnotte.</p>
                </>
              ) : (
                <>
                  <UserPlus size={40} style={{ color: 'var(--gold)', marginBottom: 12 }} />
                  <p style={{ fontWeight: 600 }}>{result.message}</p>
                  {result.invite_link && (
                    <div style={{ marginTop: 12 }}>
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>Partagez ce lien d'inscription :</p>
                      <div style={{
                        background: 'var(--bg-secondary)', borderRadius: 8, padding: '10px 14px',
                        display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)',
                      }}>
                        <span style={{ flex: 1, fontSize: '0.75rem', wordBreak: 'break-all', color: 'var(--text-muted)' }}>
                          {result.invite_link}
                        </span>
                        <button className="btn-icon" onClick={() => {
                          navigator.clipboard.writeText(result.invite_link);
                          toast.success('Lien copié !');
                        }}>
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
              <button className="btn btn-outline" style={{ marginTop: 16 }} onClick={onClose}>Fermer</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Detail View ─────────────────────────────────────────────────────────
export default function CagnotteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);

  const [cagnotte, setCagnotte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('matrix');

  const [editOpen, setEditOpen] = useState(false);
  const [cotisOpen, setCotisOpen] = useState(false);
  const [opOpen, setOpOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editOp, setEditOp] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Get(`/cagnottes/${id}`, token);
      if (res && !res.error) setCagnotte(res);
      else toast.error(res?.error || 'Erreur de chargement');
    } catch { } finally { setLoading(false); }
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = cagnotte?.admin_id === user?.id;
  const devise = cagnotte?.devise || 'FCFA';

  // ─── Actions ─────────────────────────────────────────────────────────────
  const handleEdit = async (data) => {
    const res = await Update(`/cagnottes/${id}`, data, token);
    if (res?.cagnotte) { toast.success('Cagnotte mise à jour'); load(); }
    else toast.error(res?.error || 'Erreur');
  };

  const handleCotisation = async (data) => {
    const res = await Send('/cotisations', { ...data, cagnotte_id: id }, true, token);
    if (res?.cotisation) { toast.success('Cotisation enregistrée !'); load(); }
    else { toast.error(res?.error || 'Erreur'); throw new Error(res?.error); }
  };

  const handleDeleteCotisation = async (ct) => {
    if (!window.confirm('Supprimer cette cotisation ?')) return;
    const res = await Remove(`/cotisations/${ct.id}`, token);
    if (res?.message) { toast.success('Cotisation supprimée'); load(); }
    else toast.error(res?.error || 'Erreur');
  };

  const handleOperation = async (data) => {
    if (editOp) {
      const res = await Update(`/cagnotte-operations/${editOp.id}`, data, token);
      if (res?.operation) { toast.success('Opération modifiée'); setEditOp(null); load(); }
      else { toast.error(res?.error || 'Erreur'); throw new Error(res?.error); }
    } else {
      const res = await Send('/cagnotte-operations', { ...data, cagnotte_id: id }, true, token);
      if (res?.operation) { toast.success('Opération enregistrée'); load(); }
      else { toast.error(res?.error || 'Erreur'); throw new Error(res?.error); }
    }
  };

  const handleDeleteOp = async (op) => {
    if (!window.confirm('Supprimer cette opération ?')) return;
    const res = await Remove(`/cagnotte-operations/${op.id}`, token);
    if (res?.message) { toast.success('Opération supprimée'); load(); }
    else toast.error(res?.error || 'Erreur');
  };

  const handleInvite = async (email) => {
    const res = await Send(`/cagnottes/${id}/invite`, { email }, true, token);
    if (res?.type) { if (res.type === 'added') load(); return res; }
    toast.error(res?.error || 'Erreur');
    return null;
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm(t.cagnotte.removeMemberConfirm)) return;
    const res = await Remove(`/cagnottes/${id}/members/${memberId}`, token);
    if (res?.message) { toast.success('Membre retiré'); load(); }
    else toast.error(res?.error || 'Erreur');
  };

  if (loading) return <FullScreenLoader message="Chargement de la cagnotte..." />;
  if (!cagnotte) return (
    <div className="page-content page-enter" style={{ textAlign: 'center', padding: 48 }}>
      <AlertCircle size={48} style={{ color: 'var(--danger)', marginBottom: 16 }} />
      <h3>Cagnotte introuvable</h3>
      <button className="btn btn-outline" onClick={() => navigate('/cagnotte')}>Retour</button>
    </div>
  );

  const isPositive = (cagnotte.solde ?? 0) >= 0;

  const tabs = [
    { key: 'matrix', label: '📊 Cotisations', count: cagnotte.cotisations?.length },
    { key: 'ops', label: '💸 Opérations', count: cagnotte.operations?.length },
    { key: 'members', label: '👥 Membres', count: cagnotte.members_details?.length },
  ];

  return (
    <div className="page-content page-enter">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button className="btn btn-outline btn-sm" onClick={() => navigate('/cagnotte')} style={{ marginBottom: 16 }}>
          <ArrowLeft size={14} /> Retour
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <PiggyBank size={22} style={{ color: 'var(--gold)' }} />
              <h1 style={{ margin: 0 }}>{cagnotte.libelle}</h1>
              {isAdmin && (
                <span style={{ fontSize: '0.72rem', background: 'rgba(245,197,24,0.15)', color: 'var(--gold)', borderRadius: 20, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Crown size={10} /> Admin
                </span>
              )}
              <span className={`badge ${cagnotte.statut === 'active' ? 'badge-success' : 'badge-gold'}`}>{cagnotte.statut}</span>
            </div>
            {cagnotte.description && <p style={{ margin: '4px 0 0', color: 'var(--text-muted)' }}>{cagnotte.description}</p>}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btn-outline btn-sm" onClick={load}><RefreshCw size={13} /> Actualiser</button>
            {isAdmin && <button className="btn btn-outline btn-sm" onClick={() => setEditOpen(true)}><Edit2 size={13} /> Modifier</button>}
            <button className="btn btn-outline btn-sm" onClick={() => setInviteOpen(true)}><UserPlus size={13} /> Inviter</button>
            <button className="btn btn-primary btn-sm" onClick={() => setCotisOpen(true)}><Coins size={13} /> Ma cotisation</button>
            <button className="btn btn-outline btn-sm" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }} onClick={() => { setEditOp(null); setOpOpen(true); }}>
              <ArrowDownCircle size={13} /> Opération
            </button>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="kpi-grid-2" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total collecté', num: cagnotte.total_collecte, fmt: n => formatAmount(n, devise), color: 'var(--success)', bg: 'rgba(16,185,129,0.1)', icon: '💰' },
          { label: 'Total dépensé', num: cagnotte.total_depenses, fmt: n => formatAmount(n, devise), color: 'var(--danger)', bg: 'rgba(239,68,68,0.1)', icon: '💸' },
          { label: 'Solde disponible', num: cagnotte.solde, fmt: n => `${n >= 0 ? '+' : ''}${formatAmount(n, devise)}`, color: isPositive ? 'var(--gold)' : 'var(--danger)', bg: isPositive ? 'rgba(245,197,24,0.1)' : 'rgba(239,68,68,0.1)', icon: isPositive ? '📈' : '📉' },
          { label: 'Membres', num: cagnotte.members_details?.length || 0, fmt: n => String(n), color: 'var(--primary)', bg: 'rgba(16,185,129,0.1)', icon: '👥' },
        ].map((kpi, i) => (
          <div key={i} className="hover-lift" style={{
            background: kpi.bg, border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            padding: '16px 20px', '--i': i,
          }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 6 }}>{kpi.icon}</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: kpi.color }}><AnimatedNumber value={kpi.num} format={kpi.fmt} /></div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Bank info */}
      <BankInfoCard cagnotte={cagnotte} t={t} />

      {/* Tabs */}
      <div className="tabs-scroll" style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {tabs.map(tab_ => (
          <button
            key={tab_.key}
            type="button"
            onClick={() => setTab(tab_.key)}
            style={{
              padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
              color: tab === tab_.key ? 'var(--primary)' : 'var(--text-muted)',
              borderBottom: tab === tab_.key ? '2px solid var(--primary)' : '2px solid transparent',
              fontWeight: tab === tab_.key ? 700 : 400, fontSize: '0.875rem',
              transition: 'all 0.2s', whiteSpace: 'nowrap',
            }}
          >
            {tab_.label}
            {tab_.count > 0 && (
              <span style={{
                marginLeft: 6, background: tab === tab_.key ? 'var(--primary)' : 'var(--border)',
                color: tab === tab_.key ? 'white' : 'var(--text-muted)',
                borderRadius: 20, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700,
              }}>
                {tab_.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content: Matrix */}
      {tab === 'matrix' && (
        <div className="card tab-panel">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: '1.1rem' }}>📊</span>
            <span style={{ fontWeight: 700 }}>{t.cagnotte.matrixTitle} — 6 derniers mois</span>
          </div>
          {cagnotte.members_details?.length > 0 ? (
            <ContributionMatrix
              members={cagnotte.members_details}
              cotisations={cagnotte.cotisations || []}
              devise={devise}
              isAdmin={isAdmin}
              onDelete={isAdmin ? handleDeleteCotisation : null}
            />
          ) : (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>Aucun membre pour afficher la matrice.</p>
          )}
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={() => setCotisOpen(true)}>
              <Coins size={13} /> Enregistrer une cotisation
            </button>
          </div>
        </div>
      )}

      {/* Tab content: Operations */}
      {tab === 'ops' && (
        <div className="card tab-panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem' }}>💸</span>
              <span style={{ fontWeight: 700 }}>Opérations — Utilisation des fonds</span>
            </div>
            <button className="btn btn-outline btn-sm" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }} onClick={() => { setEditOp(null); setOpOpen(true); }}>
              <ArrowDownCircle size={13} /> Ajouter
            </button>
          </div>

          {cagnotte.operations?.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Libellé</th>
                    <th>Montant</th>
                    <th>Date</th>
                    <th>Note</th>
                    {isAdmin && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {cagnotte.operations.map(op => {
                    const typeMap = { depense: { label: 'Dépense', color: 'var(--danger)', bg: 'rgba(239,68,68,0.1)' }, retrait: { label: 'Retrait', color: 'var(--gold)', bg: 'rgba(245,197,24,0.1)' }, remboursement: { label: 'Remboursement', color: 'var(--success)', bg: 'rgba(16,185,129,0.1)' }, autre: { label: 'Autre', color: 'var(--text-muted)', bg: 'rgba(100,100,100,0.1)' } };
                    const typeStyle = typeMap[op.type] || typeMap.autre;
                    return (
                      <tr key={op.id}>
                        <td>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, background: typeStyle.bg, color: typeStyle.color }}>
                            {typeStyle.label}
                          </span>
                        </td>
                        <td><strong>{op.libelle}</strong></td>
                        <td className="number-format" style={{ color: 'var(--danger)', fontWeight: 700 }}>
                          −{formatAmount(op.montant, devise)}
                        </td>
                        <td>{op.date}</td>
                        <td style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{op.note || '—'}</td>
                        {isAdmin && (
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="btn-icon" onClick={() => { setEditOp(op); setOpOpen(true); }}><Edit2 size={14} /></button>
                              <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => handleDeleteOp(op)}><Trash2 size={14} /></button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>Aucune opération enregistrée.</p>
          )}
        </div>
      )}

      {/* Tab content: Members */}
      {tab === 'members' && (
        <div className="card tab-panel">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem' }}>👥</span>
              <span style={{ fontWeight: 700 }}>Membres de la cagnotte</span>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => setInviteOpen(true)}>
              <UserPlus size={13} /> Inviter
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {cagnotte.members_details?.map(member => {
              const memberCotisations = (cagnotte.cotisations || []).filter(ct => ct.user_id === member.id);
              const memberTotal = memberCotisations.reduce((acc, ct) => acc + Number(ct.montant || 0), 0);
              const memberIsAdmin = member.id === cagnotte.admin_id;
              return (
                <div key={member.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    background: memberIsAdmin ? 'linear-gradient(135deg, var(--gold), #D97706)' : 'linear-gradient(135deg, var(--primary), var(--gold))',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, color: 'white', fontSize: '0.85rem',
                  }}>
                    {member.prenom?.[0]}{member.nom?.[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }}>{member.prenom} {member.nom}</span>
                      {memberIsAdmin && (
                        <span style={{ fontSize: '0.7rem', background: 'rgba(245,197,24,0.15)', color: 'var(--gold)', borderRadius: 20, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Crown size={9} /> Admin
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{member.email}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, color: memberTotal > 0 ? 'var(--success)' : 'var(--text-muted)' }}>
                      {formatAmount(memberTotal, devise)}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {memberCotisations.length} cotisation(s)
                    </div>
                  </div>
                  {isAdmin && !memberIsAdmin && (
                    <button className="btn-icon" style={{ color: 'var(--danger)', flexShrink: 0 }} onClick={() => handleRemoveMember(member.id)} title="Retirer le membre">
                      <X size={16} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modals */}
      <ModalCagnotte open={editOpen} onClose={() => setEditOpen(false)} onSave={handleEdit} initial={cagnotte} />
      <ModalCotisation
        open={cotisOpen}
        onClose={() => setCotisOpen(false)}
        onSave={handleCotisation}
        members={cagnotte.members_details || []}
        isAdmin={isAdmin}
        currentUserId={user?.id}
        devise={devise}
      />
      <ModalOperation
        open={opOpen}
        onClose={() => { setOpOpen(false); setEditOp(null); }}
        onSave={handleOperation}
        initial={editOp}
        devise={devise}
      />
      <ModalInvite open={inviteOpen} onClose={() => setInviteOpen(false)} onInvite={handleInvite} />
    </div>
  );
}
